/**
 * @namespace P3.utils
 */
export {default as ObjectPool} from "./ObjectPool";
export {default as BreadthFirst} from "./BreadthFirst";

/**
 * Creates an identical copy of an object.
 * http://speakingjs.com/es5/ch17.html#code_copyOwnPropertiesFrom
 *
 * @param {Object} original
 * @param {boolean} [deep=true]
 * @returns {Object}
 */
export function copyObject(original, deep = true) {
    const copy = Object.create(Object.getPrototypeOf(original));
    copyOwnPropertiesFrom(copy, original, deep);
    return copy;
}

/**
 * Copies all of an object's properties to the target object.
 * http://speakingjs.com/es5/ch17.html#code_copyOwnPropertiesFrom
 *
 * @param {Object} target
 * @param {Object} source
 * @param {boolean} [deep=true]
 * @returns {Object}
 */
export function copyOwnPropertiesFrom(target, source, deep = true) {
    Object.getOwnPropertyNames(source)
        .forEach(function (key) {
            const descriptor = Object.getOwnPropertyDescriptor(source, key);
            Object.defineProperty(target, key, descriptor);
            if (deep && typeof descriptor.value === "object" && descriptor.value !== null) {
                target[key] = P3.utils.copyObject(source[key], deep);
            }
        });
    return target;
}

/**
 * Returns a value converted to a zero padded string.
 *
 * @param {number} n - The value to pad.
 * @param {number} [width=0] - The number of zeros to pad the string with.
 * @returns {string}
 */
export function padNum(n, width = 0) {
    const num = Math.abs(n);
    const zeros = Math.max(0, width - Math.floor(num).toString().length);
    let zeroStr = Math.pow(10, zeros).toString().substr(1);
    if (num < 0) {
        zeroStr = "-" + zeroStr;
    }
    return zeroStr + num;
}

/**
 * @param {Array} arr
 * @returns {*}
 */
export function selectRandom(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * @param {Array} arr
 * @returns {array}
 */
export function shuffleArray(arr) {
    let i = arr.length;
    let t = null;
    let r = -1;

    while (0 !== i) {
        r = Math.floor(Math.random() * i);
        t = arr[-- i];
        arr[i] = arr[r];
        arr[r] = t;
    }
    return arr;
}

/**
 * @returns {boolean}
 */
export function isCocoonJS() {
    return (typeof navigator["isCocoonJS"] !== "undefined");
}

/**
 * Returns an array of pre defined length with optional default values.
 * @param {number} length - Array length.
 * @param {*} [defaultValue=null] - Default value.
 * @returns {Array}
 */
export function array(length, defaultValue = null) {
    const arr = [];
    for (let i = 0; i < length; i++) arr.push(defaultValue);
    return arr;
}

/**
 * Returns a multi-dimensional array of pre defined size with optional default values.
 * @param {number} rows - Number of rows.
 * @param {number} columns - Number of columns.
 * @param {*} [defaultValue=null] - Default value.
 * @returns {Array<Array>}
 */
export function marray(rows, columns, defaultValue = null) {
    const y = [];
    for (let i = 0; i < rows; i++) {
        const x = [];
        for (let j = 0; j < columns; j++) x[j] = defaultValue;
        y[i] = x;
    }
    return y;
}

/**
 * @param {PIXI.Texture} texture
 * @param {PIXI.Container} [parent]
 * @param {PIXI.Point} [anchor]
 * @returns {PIXI.Sprite}
 */
export function sprite(texture, parent, anchor) {
    if(texture instanceof PIXI.Texture === false) {
        console.warn("arg is not of type PIXI.Texture : utils.sprite");
        texture = new PIXI.Graphics().beginFill(0xff0000, 0.5).drawRect(0, 0, 125, 125).endFill().generateTexture(); 
    }
    anchor = anchor || new PIXI.Point();
    const sprite = new PIXI.Sprite(texture);
    sprite.anchor = anchor;
    parent && parent.addChild(sprite);
    return sprite;
}

/**
 * Checks if first argument is fully within the bounds of the 2nd
 * @param displayObject {PIXI.DisplayObject} - Display object your testing
 * @param boundingObj {PIXI.DisplayObject} - Display object your testing against
 * @param [hitSide] {Object} - Optional param, object will contain which side was hit, or null for no hit
 * @returns {boolean}
 */
export function boundsCheck(displayObject, boundingObj, hitSide) {
    if (!displayObject || !boundingObj || !displayObject.getBounds || !boundingObj.getBounds)  throw new Error("both args must be PIXI.DisplayObject : utils.boundsCheck");
    const dispObj = displayObject.getBounds();

    const topLeftPoint = {
        x: dispObj.x,
        y: dispObj.y
    };
    const topRightPoint = {
        x: dispObj.x + dispObj.width,
        y: dispObj.y
    };

    const botLeftPoint = {
        x: dispObj.x,
        y: dispObj.y + dispObj.height
    };
    const botRightPoint = {
        x: dispObj.x + dispObj.width,
        y: dispObj.y + dispObj.height
    };

    if (hitSide) {//hit side is an object so can return by reference
        if (!boundingObj.containsPoint(topLeftPoint) && !boundingObj.containsPoint(botLeftPoint) && boundingObj.containsPoint(topRightPoint) && boundingObj.containsPoint(botRightPoint)) {
            hitSide.side = "left";
        }
        else if (!boundingObj.containsPoint(topRightPoint) && !boundingObj.containsPoint(botRightPoint) && boundingObj.containsPoint(topLeftPoint) && boundingObj.containsPoint(botLeftPoint)) {
            hitSide.side = "right";
        }
        else if (!boundingObj.containsPoint(topLeftPoint) && !boundingObj.containsPoint(topRightPoint) && boundingObj.containsPoint(botLeftPoint) && boundingObj.containsPoint(botRightPoint)) {
            hitSide.side = "top";
        }
        else if (!boundingObj.containsPoint(botLeftPoint) && !boundingObj.containsPoint(botRightPoint) && boundingObj.containsPoint(topLeftPoint) && boundingObj.containsPoint(topRightPoint)) {
            hitSide.side = "bottom";
        }
    }

    return ((boundingObj.containsPoint(topLeftPoint)
    && boundingObj.containsPoint(topRightPoint)
    && boundingObj.containsPoint(botLeftPoint)
    && boundingObj.containsPoint(botRightPoint)));
}

export function collide(shape1, shape2) {
    //--broad phase--//
    if (collideAABB(shape1, shape2)) {
        //--narrow phase--//
        const result = collideSAT(shape1, shape2);
        if (result) return result;
    }
    return false;
};

export function collideAABB(r1, r2, scale = new PIXI.Point(1, 1)) {
    r1 = r1.getBounds();
    r2 = r2.getBounds();

    return !(r2.x > (r1.x + r1.width * scale.x) ||
    (r2.x + r2.width * scale.x) < r1.x ||
    r2.y > (r1.y + r1.height * scale.y) ||
    (r2.y + r2.height * scale.y) < r1.y);
};


/**
 * Collision testing with rotation, also returns overlap if a collision occurs, should be able to use overlap
 * to push the sprite out of whatever it collided with. Can take a normal sprite or display object with a list
 * of points (the vertices of your polygon as global coordinates, you must maintain this list).
 *
 * Original source - https://gist.github.com/louisstow/807250 - added fixes and adapted to PIXI
 * @param poly1 {PIXI.Sprite|PIXI.DisplayObject}
 * @param poly2 {PIXI.Sprite|PIXI.DisplayObject}
 * @returns {Object|Boolean}
 */
export function collideSAT(poly1, poly2) {
    const points1 = poly1.points === undefined ? convertVertsToVectors(poly1.vertexData) : convertVertsToVectors(poly1.points);
    const points2 = poly2.points === undefined ? convertVertsToVectors(poly2.vertexData) : convertVertsToVectors(poly2.points);

    let l = points1.length,
        k = points2.length,
        normal = {x: 0, y: 0},
        length,
        min1, min2,
        max1, max2,
        interval,
        MTV,
        dot,
        nextPoint,
        currentPoint;

    //loop through the edges of Polygon 1
    for (let i = 0; i < l; i++) {
        nextPoint = points1[(i === l - 1 ? 0 : i + 1)];
        currentPoint = points1[i];

        //generate the normal for the current edge
        normal.x = -(nextPoint[1] - currentPoint[1]);
        normal.y = (nextPoint[0] - currentPoint[0]);

        //normalize the vector
        length = Math.sqrt(normal.x * normal.x + normal.y * normal.y);
        normal.x /= length;
        normal.y /= length;

        //default min max
        min1 = min2 = -1;
        max1 = max2 = -1;

        //project all vertices from poly1 onto axis
        for (let j = 0; j < l; ++j) {
            dot = points1[j][0] * normal.x + points1[j][1] * normal.y;
            if (dot > max1 || max1 === -1) max1 = dot;
            if (dot < min1 || min1 === -1) min1 = dot;
        }

        //project all vertices from poly2 onto axis
        for (let j = 0; j < k; ++j) {
            dot = points2[j][0] * normal.x + points2[j][1] * normal.y;
            if (dot > max2 || max2 === -1) max2 = dot;
            if (dot < min2 || min2 === -1) min2 = dot;
        }

        //calculate the minimum translation vector should be negative
        interval = (min1 < min2) ? min2 - max1 : min1 - max2;

        //exit early if positive
        if (interval > 0) {
            return false;
        }
        if (interval > MTV) MTV = interval;
    }

    //loop through the edges of Polygon 1
    for (let i = 0; i < k; i++) {
        nextPoint = points2[(i === k - 1 ? 0 : i + 1)];
        currentPoint = points2[i];

        //generate the normal for the current edge
        normal.x = -(nextPoint[1] - currentPoint[1]);
        normal.y = (nextPoint[0] - currentPoint[0]);

        //normalize the vector
        length = Math.sqrt(normal.x * normal.x + normal.y * normal.y);
        normal.x /= length;
        normal.y /= length;

        //default min max
        min1 = min2 = -1;
        max1 = max2 = -1;

        //project all vertices from poly1 onto axis
        for (let j = 0; j < l; ++j) {
            dot = points1[j][0] * normal.x + points1[j][1] * normal.y;
            if (dot > max1 || max1 === -1) max1 = dot;
            if (dot < min1 || min1 === -1) min1 = dot;
        }

        //project all vertices from poly2 onto axis
        for (let j = 0; j < k; ++j) {
            dot = points2[j][0] * normal.x + points2[j][1] * normal.y;
            if (dot > max2 || max2 === -1) max2 = dot;
            if (dot < min2 || min2 === -1) min2 = dot;
        }

        //calculate the minimum translation vector should be negative
        interval = (min1 < min2) ? min2 - max1 : min1 - max2;

        //exit early if positive
        if (interval > 0) {
            return false;
        }
        if (interval > MTV) MTV = interval;
    }

    return {overlap: MTV};

    function convertVertsToVectors(verts) {
        let arr = P3.utils.marray(4, 2);

        let count = 0;
        for (let i = 0; i < verts.length; i += 2) {
            arr[count][0] = verts[i];
            arr[count][1] = verts[i + 1];
            count++;
        }

        return arr;
    }
}